import { safeLocalStorage } from "./safeStorage";
export interface FeatureFlags {
  // EXPERIENCE_PAGE: boolean;
  // INTERACT_PAGE: boolean;
  DESIGN_SYSTEM_SHOWCASE: boolean;
  /** Settings > Organization > Data sensitivity: review and decide column sensitivity classes. */
  SENSITIVITY_REVIEW: boolean;
  /** Distribution Simple mode shows Advanced's Split by and People steps inline (a member can switch back to the classic setup). */
  DISTRIBUTION_SIMPLE_STEPS: boolean;
  [key: string]: boolean;
}

const defaultFeatureFlags: FeatureFlags = {
  // EXPERIENCE_PAGE: false,
  // INTERACT_PAGE: false,
  DESIGN_SYSTEM_SHOWCASE: false,
  SENSITIVITY_REVIEW: false,
  DISTRIBUTION_SIMPLE_STEPS: true,
};

const STORAGE_KEY = "featureFlags";

/**
 * Query params QA can use to switch a flag for their own browser, e.g.
 * `/distribution?simpleSteps=0`. The value is persisted like a Settings toggle.
 */
const QUERY_PARAM_FLAGS: Record<string, keyof FeatureFlags> = {
  simpleSteps: "DISTRIBUTION_SIMPLE_STEPS",
};

/** Retired switch (old auth screens): dropped from URLs, and from storage on startup. */
const RETIRED_QUERY_PARAMS = ["newAuth"];
const RETIRED_STORED_FLAGS = ["NEW_AUTH_UI", "NEW_LANDING_PAGE"];

const readEnvFlags = (): Partial<FeatureFlags> => {
  const envFlags: Partial<FeatureFlags> = {};
  Object.keys(defaultFeatureFlags).forEach((key) => {
    const envValue = process.env[`REACT_APP_FEATURE_${key}`];
    if (envValue !== undefined) {
      envFlags[key] = envValue.toLowerCase() === "true";
    }
  });
  return envFlags;
};

/** Per-browser overrides only — never the resolved set, so env rollouts still apply. */
const readStoredOverrides = (): Partial<FeatureFlags> => {
  const storedFlags = safeLocalStorage.getItem(STORAGE_KEY);
  if (!storedFlags) return {};
  try {
    const parsed = JSON.parse(storedFlags);
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch (error) {
    console.warn("Invalid feature flags in localStorage, using defaults");
    return {};
  }
};

/**
 * Beta flags the organization lets this member turn on. `null` (the start, and always for
 * developers) masks nothing. Set by `useApplyBetaFeatureMask` after the allow-list loads.
 */
let allowedBetaFlags: ReadonlySet<string> | null = null;
let maskedBetaFlags: readonly string[] = [];

/**
 * Registry flags the member's own override may not switch unless the organization allows them.
 * Defaults and env rollouts are untouched; a stored override of a removed key is ignored, not
 * deleted. Pass `null` to mask nothing.
 */
export const setAllowedBetaFlags = (
  allowed: readonly string[] | null,
  registryFlags: readonly string[] = [],
): void => {
  allowedBetaFlags = allowed === null ? null : new Set(allowed);
  maskedBetaFlags = registryFlags;
};

const withoutDisallowedOverrides = (overrides: Partial<FeatureFlags>): Partial<FeatureFlags> => {
  if (allowedBetaFlags === null) return overrides;
  const allowed = allowedBetaFlags;
  const kept = { ...overrides };
  maskedBetaFlags.forEach((flag) => {
    if (!allowed.has(flag)) delete kept[flag];
  });
  return kept;
};

export const getFeatureFlags = (): FeatureFlags => ({
  ...defaultFeatureFlags,
  ...readEnvFlags(),
  ...withoutDisallowedOverrides(readStoredOverrides()),
});

export const setFeatureFlags = (flags: Partial<FeatureFlags>): void => {
  const newOverrides = { ...readStoredOverrides(), ...flags };
  safeLocalStorage.setItem(STORAGE_KEY, JSON.stringify(newOverrides));
};

export const isFeatureEnabled = (feature: keyof FeatureFlags): boolean => {
  const flags = getFeatureFlags();
  return flags[feature] || false;
};

export const useFeatureFlag = (feature: keyof FeatureFlags): boolean => {
  return isFeatureEnabled(feature);
};

const parseQueryFlagValue = (value: string): boolean | undefined => {
  if (value === "1" || value === "true") return true;
  if (value === "0" || value === "false") return false;
  return undefined;
};

/**
 * Persists any `QUERY_PARAM_FLAGS` found in `search` and returns the search
 * string without them. Must run before the first render: flags are read
 * synchronously and are not reactive.
 */
export const applyFeatureFlagQueryOverrides = (search: string): string => {
  const params = new URLSearchParams(search);
  const overrides: Partial<FeatureFlags> = {};

  RETIRED_QUERY_PARAMS.forEach((param) => params.delete(param));

  Object.entries(QUERY_PARAM_FLAGS).forEach(([param, flag]) => {
    const raw = params.get(param);
    if (raw === null) return;
    params.delete(param);
    const value = parseQueryFlagValue(raw.toLowerCase());
    if (value !== undefined) overrides[flag] = value;
  });

  if (Object.keys(overrides).length > 0) setFeatureFlags(overrides);

  const remaining = params.toString();
  return remaining ? `?${remaining}` : "";
};

/**
 * Removes retired flag keys (e.g. the old `NEW_AUTH_UI`) from this browser's stored
 * overrides, keeping every other stored flag. Runs once at startup.
 */
export const removeRetiredStoredFlags = (): void => {
  const stored = readStoredOverrides();
  if (!RETIRED_STORED_FLAGS.some((key) => key in stored)) return;
  RETIRED_STORED_FLAGS.forEach((key) => delete stored[key]);
  safeLocalStorage.setItem(STORAGE_KEY, JSON.stringify(stored));
};

/** Old entry links: `/v1` (old screens, now the same as `/v2`) and `/v2`; each also as `/v<n>/<route>`. */
const AUTH_ENTRY_PATHS = ["/v2", "/v1"];

/**
 * `/v1` and `/v2` are aliases: returns the real path to show: `/login` for the bare
 * entry, or the rest of the path (`/v2/login/magic` → `/login/magic`; the emailed
 * sign-in link is `/v2/login/magic?token=…`). Returns null for every other path.
 */
export const applyFeatureFlagPathOverrides = (pathname: string): string | null => {
  const entry = AUTH_ENTRY_PATHS.find((path) => pathname === path || pathname.startsWith(`${path}/`));
  if (entry === undefined) return null;
  const rest = pathname.slice(entry.length);
  return rest === "" || rest === "/" ? "/login" : rest;
};

/**
 * Both overrides for the URL the app was opened with, before the first render.
 * Returns the URL to replace it with (path rewritten, flag params dropped,
 * every other param and the hash kept — e.g. the emailed sign-in link
 * `/v2/login/magic?token=…` → `/login/magic?token=…`), or null when nothing
 * changed.
 */
export const applyFeatureFlagUrlOverrides = ({
  pathname,
  search,
  hash,
}: Pick<Location, "pathname" | "search" | "hash">): string | null => {
  removeRetiredStoredFlags();
  const entryPath = applyFeatureFlagPathOverrides(pathname);
  const remainingSearch = applyFeatureFlagQueryOverrides(search);
  if (entryPath === null && remainingSearch === search) return null;
  return `${entryPath ?? pathname}${remainingSearch}${hash}`;
};

export const toggleFeature = (feature: keyof FeatureFlags): void => {
  const currentValue = isFeatureEnabled(feature);
  setFeatureFlags({ [feature]: !currentValue });
  window.location.reload();
};

export const enableAllFeatures = (): void => {
  const allFeatures: FeatureFlags = {} as FeatureFlags;
  Object.keys(defaultFeatureFlags).forEach((key) => {
    allFeatures[key as keyof FeatureFlags] = true;
  });
  setFeatureFlags(allFeatures);
  window.location.reload();
};

export const disableAllFeatures = (): void => {
  setFeatureFlags(defaultFeatureFlags);
  window.location.reload();
};
